import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "w-full rowend" }
const _hoisted_2 = { class: "es-special-days mtop-10" }
const _hoisted_3 = { class: "row pad-m gap-08" }
const _hoisted_4 = { class: "col-0-13-5 w-full" }
const _hoisted_5 = {
  key: 0,
  class: "col-1-5"
}
const _hoisted_6 = {
  key: 1,
  class: "col-1-5"
}
const _hoisted_7 = { class: "font-m" }
const _hoisted_8 = { class: "col-1" }

import {computed, nextTick, ref, watch} from "vue";
import {
  DayModelLightResponse,
  DeleteSpecialDayForInstituteRequest,
  PersonLightResponse,
  SettingsApi,
  type SpecialDayRequest,
  SpecialDayResponse,
  UpdateSpecialDayForInstituteRequest
} from "@/apiGenerate/medserver";
import {medServerApiConfig} from "@/config/medserver-api.config";
import EsSpecialDaysInputForm from "@/components/form/esSpecialDaysInputForm.vue";
import EsSelect from "@/components/esSelect.vue";
import EsButtonIcon from "@/components/esButtonIcon.vue";
import {ApiError, ItemSelect} from '@/models/share.model';
import {useI18n} from "vue-i18n";
import EsConfirmDialog from "@/components/modals/esConfirmDialog.vue";
import { DateFormat } from "@/models/share.model";
import { useToast } from 'primevue';
import { toastError, toastSuccess, toastWarn } from '@/utils/toast-utils';
import { format } from "date-fns";
import { useAppUserStore } from "@/stores/appUser";
import { getLocaleForDateFns } from "@/utils/date-utils";

type SpecialDayElement = SpecialDayResponse & { dirty: boolean };


export default /*@__PURE__*/_defineComponent({
  __name: 'esSpecialDaysSettings',
  props: {
    selectedInstituteId: {},
    refreshData: { type: Boolean },
    selectedHealthPro: {}
  },
  emits: ['refreshed', 'special-days-changed'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const {t} = useI18n();
const toast = useToast();
const appUserStore = useAppUserStore();

const settingsApi = new SettingsApi(medServerApiConfig);

const days = ref<Array<SpecialDayElement>>([]);
const dayModels = ref<Array<DayModelLightResponse>>([]);

const openModal = ref<boolean>(false);

const dayModelsItemSelect = computed(() => dayModels.value.map(toItemSelect));
const isConfirmOpened = ref(false);
const confirmData = ref<SpecialDayElement>();

const emits = __emit;

const local = computed(() => {
  return getLocaleForDateFns(appUserStore?.currentAppUserData?.preferences?.menuLanguageIso);
});

const currentPersonId = computed(() => {
  if (!appUserStore.currentAppUserData || !appUserStore.currentAppUserData.healthGroupMemberships) {return null}
  return appUserStore.currentAppUserData.healthGroupMemberships[0].pwr.personWithRoleId
});

async function refreshDayModels(instituteId: string) {
  if (instituteId && (currentPersonId.value || props.selectedHealthPro?.id)) {
    settingsApi.findDayModelByInstitute({instituteId: props.selectedInstituteId, doctorId: props.selectedHealthPro?.id ? props.selectedHealthPro.id : currentPersonId.value}).then((res: Array<DayModelLightResponse>) => {
      if (res) dayModels.value = res;
    }).catch(() => {
      toastError(toast, {
          summary: t('common.toast.error'),
          detail: t('error.DEFAULT')
      });
    });
  }
}

async function refreshSpecialDays(instituteId: string) {
  if (instituteId && (currentPersonId.value || props.selectedHealthPro?.id)) {
    settingsApi.findSpecialDaysByInstitute({instituteId: props.selectedInstituteId, doctorId: props.selectedHealthPro?.id ? props.selectedHealthPro.id : currentPersonId.value}).then((res: Array<SpecialDayResponse>) => {
      if (res) days.value = res
            .sort((a, b) => a.periodStart.localeCompare(b.periodStart))
            .map(d => ({...d, dirty: false}));
    }).catch(() => {
      toastError(toast, {
          summary: t('common.toast.error'),
          detail: t('error.DEFAULT')
      });
    });  
  }
}

const refreshData = async () => {
  await refreshSpecialDays(props.selectedInstituteId);
  await refreshDayModels(props.selectedInstituteId);

  emits('refreshed');
}

nextTick(async () => {
  await refreshSpecialDays(props.selectedInstituteId);
  await refreshDayModels(props.selectedInstituteId);
})


const updateDate = (day: SpecialDayElement, newStartDate: string, newEndDate: string) => {
  if (!newStartDate || !newEndDate || (day.periodStart === newStartDate && day.periodEnd === newEndDate)) {
    return;
  }

  day.periodStart = format(new Date(newStartDate), DateFormat.ISO_SMALL);
  day.periodEnd = format(new Date(newEndDate), DateFormat.ISO_SMALL);
  day.dirty = true;

  commitDateUpdate(day);
};


const updateSpecialDay = async (specialDayId: string, payload: SpecialDayRequest) => {
  const request: UpdateSpecialDayForInstituteRequest = {
    instituteId: props.selectedInstituteId,
    specialdayId: specialDayId,
    specialDayRequest: payload,
  };

 settingsApi.updateSpecialDayForInstitute(request).then(async(specialDayResponse: SpecialDayResponse) => {
     if(specialDayResponse.warningCode) {
      toastWarn(toast, {
        summary: t('common.toast.success.update'),
        detail:  t('warn.' + specialDayResponse.warningCode)
      });
     } else {
      toastSuccess(toast, {
          summary: t('common.toast.success.update'),
          detail:  t('settings.label.specialDays.toast.updated')
      });
     }
     await refreshSpecialDays(props.selectedInstituteId);
     emits("special-days-changed");
  }).catch((error: ApiError) => {
    toastError(toast, {
        summary: t('common.toast.error'),
        detail: t(`error.${error.status}`)
    });
  });
} 

async function commitDateUpdate(day: SpecialDayElement) {
  if (!day?.specialDayId || !day.dirty) {
    return;
  }

  const payload: SpecialDayRequest = {
    dayModelId: day.dayModel.dayModelId,
    periodStart: day.periodStart,
    periodEnd: day.periodEnd,
  }

  await updateSpecialDay(day.specialDayId, payload);
}

async function commitDayModelUpdate(day: SpecialDayElement, dayModelId: string) {
  if (!day?.specialDayId || !dayModelId || day.dayModel.dayModelId === dayModelId) {
    return;
  }

  const payload: SpecialDayRequest = {
    dayModelId: dayModelId,
    periodStart: day.periodStart,
    periodEnd: day.periodEnd,
  }

  await updateSpecialDay(day.specialDayId, payload);
}

async function deleteSpecialDate(day: SpecialDayElement) {
  if (!day?.specialDayId) return;

  const request: DeleteSpecialDayForInstituteRequest = {
    instituteId: props.selectedInstituteId,
    specialdayId: day.specialDayId,
  };

  await settingsApi.deleteSpecialDayForInstitute(request).then(async () => {
     await refreshSpecialDays(props.selectedInstituteId);
      toastSuccess(toast, {
          summary: t('common.toast.success.delete'),
          detail:  t('settings.label.specialDays.toast.deleted')
      });
      refreshData();
      emits("special-days-changed");
  }).catch((error: ApiError) => {
    toastError(toast, {
        summary: t('common.toast.error'),
        detail: t(`error.${error.status}`)
    });

    // Throw error to set at false the var __alreadyClicked in v-one-click directive
    throw error;
  });
}

function toItemSelect(dayModel: DayModelLightResponse): ItemSelect {
  return {
    id: dayModel.dayModelId,
    label: dayModel.name,
  }
}

function openConfirm(day: SpecialDayElement) {
  if (!day?.specialDayId) return;
  confirmData.value = day;
  isConfirmOpened.value = true;
}

const closeConfirm = async (confirm: boolean) => {
  isConfirmOpened.value = false;
  if (confirm) {
    await deleteSpecialDate(confirmData.value);
    emits("special-days-changed");
  }
  confirmData.value = null;
}

const addModalClose = async (refresh: boolean)=> {
  if (refresh) {
    await refreshSpecialDays(props.selectedInstituteId);
    emits("special-days-changed");
  }
  
  openModal.value = false
}

watch(
    () => props.selectedInstituteId,
    () => {
      refreshData();
    }
);

watch(
    () => props.selectedHealthPro,
    () => {
      refreshData();
    }
);

watch(
    () => props.refreshData,
    () => {
      if (props.refreshData === true) refreshData()
    }
)

return (_ctx: any,_cache: any) => {
  const _component_es_button = _resolveComponent("es-button")!
  const _component_esInputDateTime = _resolveComponent("esInputDateTime")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_es_button, {
        label: _unref(t)('action.add'),
        onClick: _cache[0] || (_cache[0] = ($event: any) => (openModal.value = true)),
        "is-primary": true
      }, null, 8, ["label"])
    ]),
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(days.value, (day, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: "w-full gap-02"
        }, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_esInputDateTime, {
                dateTimeValue: [new Date(day.periodStart), new Date(day.periodEnd)],
                dateFormat: _unref(DateFormat).DATEPICKER,
                withTime: false,
                range: true,
                disabled: !day.editable,
                size: "small",
                onUpdateDateTime: ([start, end]: [string, string]) => updateDate(day, start, end)
              }, null, 8, ["dateTimeValue", "dateFormat", "disabled", "onUpdateDateTime"])
            ]),
            (day.editable)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createVNode(EsSelect, {
                    options: dayModelsItemSelect.value,
                    selectedData: toItemSelect(day.dayModel),
                    onChangeSelect: ($event: any) => (commitDayModelUpdate(day, ($event as ItemSelect).id))
                  }, null, 8, ["options", "selectedData", "onChangeSelect"])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(t)('settings.label.specialDays.labelQuickParam', {'specialDayDate': _unref(format)(new Date(day.dayModel.name), _unref(DateFormat).FRONT_LETTER, {locale: local.value})})), 1)
                ])),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(EsButtonIcon, {
                folder: "shareIcons",
                icon: "xmark",
                onClick: ($event: any) => (openConfirm(day))
              }, null, 8, ["onClick"])
            ])
          ])
        ]))
      }), 128)),
      _createVNode(EsSpecialDaysInputForm, {
        dayModels: dayModels.value,
        selectedHealthPro: props.selectedHealthPro,
        selectedInstituteId: props.selectedInstituteId,
        isOpen: openModal.value,
        header: 'settings.label.specialDays.modal.add.title',
        onAddModalClose: _cache[1] || (_cache[1] = ($event: any) => (addModalClose($event)))
      }, null, 8, ["dayModels", "selectedHealthPro", "selectedInstituteId", "isOpen"]),
      _createVNode(EsConfirmDialog, {
        isOpen: isConfirmOpened.value,
        onModalClose: _cache[2] || (_cache[2] = () => closeConfirm(false)),
        header: _unref(t)('settings.label.specialDays.modal.delete.title'),
        message: _unref(t)('settings.label.specialDays.modal.delete.message'),
        "on-confirm": () => closeConfirm(true)
      }, null, 8, ["isOpen", "header", "message", "on-confirm"])
    ])
  ], 64))
}
}

})